import { isBefore } from '@fmnts/core/chronos';
import moment, { MomentInput } from 'moment';
import { ConditionBase, ConditionBuilder } from '../condition.type';

export interface ConditionBefore extends ConditionBase {
  /**
   * The value of the related field must come chronologically before this
   * value for the condition to be fulfilled
   */
  readonly before: moment.MomentInput;
  /**
   * Set to `true`, if the condition should be fulfilled for the same date
   *
   * @default false
   */
  readonly exclusive?: boolean;
  /**
   * Can be used to control the checks granularity.
   *
   * @default 'day'
   */
  readonly granularity?: moment.unitOfTime.StartOf;
}

/**
 * Type guard that checks if an object fulfills the `ConditionBefore` interface
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `ConditionBefore`
 */
export function isConditionBefore(
  c: Record<string, any>,
): c is ConditionBefore {
  return 'before' in c && moment(c['before'] as MomentInput, true).isValid();
}

/**
 * @param condition Condition to check
 *
 * @returns
 * Condition function that returns `true`, if the passed `value` comes
 * chronologically **before** the `before` specified in the condition.
 *
 * @example
 * before({before: '2000-01-02'})('2000-01-01') // true
 * before({before: '1999-12-31'})('2000-01-01') // false
 *
 *
 * @see {@link https://momentjs.com/docs/#/query/is-before/ }
 */
export const before: ConditionBuilder<ConditionBefore, moment.MomentInput> = (
  condition,
) => {
  const { before: other, exclusive, granularity = 'day' } = condition;

  return isBefore(other, { exclusive, granularity });
};
